import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { Clock, FileText, AlertTriangle, ArrowRight, Download } from "lucide-react";
import { getCreditReadiness } from "../lib/creditReadiness";

const POLL_INTERVAL = 4000;
const MAX_POLL_ATTEMPTS = 60; // 4 minutes — Claude Haiku 4.5 generating ~60K HTML can take 90–180s

export default function CreditReadinessReport() {
  const { id } = useParams();
  const [rec, setRec] = useState(null);
  const [error, setError] = useState("");
  const [attempts, setAttempts] = useState(0);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;

    const poll = async () => {
      try {
        const data = await getCreditReadiness(id);
        if (cancelled) return;
        setRec(data);
        if (data.report_status === "ready" || data.report_status === "error") return;
        attemptsRef.current += 1;
        setAttempts(attemptsRef.current);
        if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
          setError("Report generation is taking longer than expected. Please check back shortly.");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.detail || "Could not load report.");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (error || rec?.report_status === "error") {
    return (
      <div data-testid="cr-report-error" className="max-w-2xl mx-auto px-6 pt-32 pb-24 text-center">
        <AlertTriangle size={56} style={{ color: "#cc7755", margin: "0 auto 1.5rem" }} />
        <h1 className="font-display text-4xl md:text-5xl mb-6 leading-[0.95]">Report generation issue.</h1>
        <p className="text-base mb-8" style={{ color: "var(--ink-muted)" }}>
          {error || rec?.report_error || "Please contact support@wealthfirstinc.tv"}
        </p>
        <Link to="/credit-readiness" className="btn-brass" style={{ justifyContent: "center" }}>
          Back <ArrowRight size={14} />
        </Link>
      </div>
    );
  }

  if (!rec || rec.report_status !== "ready") {
    const stages = [
      "Scanning compliance posture…",
      "Matching against WealthFirst Lenders Database…",
      "Computing approval-score odds…",
      "Drafting 90-day action roadmap…",
    ];
    const stage = stages[Math.min(stages.length - 1, Math.floor(attempts / 4))];
    return (
      <div data-testid="cr-report-loading" className="max-w-2xl mx-auto px-6 pt-32 pb-24 text-center">
        <Clock size={56} style={{ color: "var(--brass)", margin: "0 auto 1.5rem" }} />
        <p className="eyebrow mb-4">WealthFirst FundingBot Working</p>
        <h1 className="font-display text-4xl md:text-5xl mb-6 leading-[0.95]">
          Analyzing your business profile…
        </h1>
        <p className="text-base" style={{ color: "var(--ink-muted)" }}>
          {stage}
        </p>
      </div>
    );
  }

  const score = rec.approval_score;
  const verdict = rec.verdict || "—";
  const inputs = rec.inputs || {};
  const sanitized = DOMPurify.sanitize(rec.report_html || "", {
    ALLOWED_TAGS: ["h1", "h2", "h3", "h4", "p", "ul", "ol", "li", "table", "thead", "tbody", "tr", "th", "td", "strong", "em", "span", "br", "hr", "blockquote"],
    ALLOWED_ATTR: ["style", "colspan", "rowspan"],
  });

  return (
    <div data-testid="cr-report-page" className="pt-24">
      {/* Header */}
      <section className="px-6 py-12" style={{ background: "var(--bg)" }}>
        <div className="max-w-5xl mx-auto">
          <p className="eyebrow mb-3" style={{ color: "var(--brass)" }}>
            WealthFirst AI Diagnostic Report
          </p>
          <h1 className="font-display text-4xl md:text-6xl leading-[0.95] mb-6">
            {inputs.business_name || "Your Business"}
          </h1>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Metric label="Approval Score" value={score !== null && score !== undefined ? `${score}/100` : "—"} highlight testid="cr-metric-score" />
            <Metric label="Verdict" value={verdict} highlight testid="cr-metric-verdict" />
            <Metric label="Target Funding" value={`$${Number(inputs.target_funding_amount_usd || 0).toLocaleString()}`} />
            <Metric label="Entity" value={`${inputs.entity_type || "—"} · ${inputs.state_of_formation || "—"}`} />
          </div>
        </div>
      </section>

      {/* Report Body */}
      <section className="px-6 py-16">
        <div
          className="max-w-4xl mx-auto cr-report"
          data-testid="cr-report-html"
          dangerouslySetInnerHTML={{ __html: sanitized }}
        />
        <style>{`
          .cr-report h2 { font-family: var(--font-display, Georgia, serif); font-size: 2rem; margin: 2.5rem 0 1rem; color: var(--ink); border-bottom: 1px solid var(--hairline); padding-bottom: 0.5rem; }
          .cr-report h3 { font-size: 1.25rem; margin: 1.5rem 0 0.75rem; color: var(--brass); text-transform: uppercase; letter-spacing: 0.1em; }
          .cr-report p { margin: 0.75rem 0; line-height: 1.7; color: var(--ink); }
          .cr-report ul, .cr-report ol { margin: 0.75rem 0 1rem 1.5rem; line-height: 1.7; }
          .cr-report li { margin: 0.4rem 0; color: var(--ink); }
          .cr-report table { width: 100%; border-collapse: collapse; margin: 1rem 0 1.5rem; font-size: 0.9rem; }
          .cr-report thead { background: rgba(194,163,115,0.1); }
          .cr-report th { text-align: left; padding: 10px 12px; border-bottom: 1px solid var(--brass); color: var(--brass); text-transform: uppercase; letter-spacing: 0.1em; font-size: 0.75rem; }
          .cr-report td { padding: 10px 12px; border-bottom: 1px solid var(--hairline); color: var(--ink); }
          .cr-report strong { color: var(--brass); }
        `}</style>
      </section>

      {/* Footer CTAs */}
      <section className="px-6 py-12" style={{ background: "var(--surface)" }}>
        <div className="max-w-4xl mx-auto flex flex-wrap gap-4 items-center justify-between">
          <p className="text-xs max-w-lg" style={{ color: "var(--ink-muted)" }}>
            <em>Generated by WealthFirst FundingBot · Claude Haiku 4.5.</em> This is a diagnostic AI assessment, not a guarantee
            of funding approval. Underwriting decisions remain with individual lenders at their sole discretion.
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => window.print()}
              className="btn-brass"
              style={{ background: "transparent", color: "var(--brass)" }}
              data-testid="cr-print"
            >
              <Download size={14} /> Print / Save PDF
            </button>
            <Link to="/funding/apply?plan=autopilot" className="btn-brass" data-testid="cr-upgrade-autopilot">
              <FileText size={14} /> Activate AutoPilot
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Metric({ label, value, highlight, testid }) {
  return (
    <div
      className="p-4"
      style={{ border: "1px solid rgba(194,163,115,0.3)", background: "rgba(194,163,115,0.05)" }}
      data-testid={testid}
    >
      <p className="text-xs uppercase tracking-[0.15em] mb-1.5" style={{ color: "var(--brass)" }}>
        {label}
      </p>
      <p className="font-display text-2xl leading-tight" style={{ color: highlight ? "var(--brass)" : "var(--ink)" }}>
        {value}
      </p>
    </div>
  );
}
